import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Select, pubnub } from 'wwtc-sdk';
import './login.css';
import './common/button.css';
import { getGatewayLanguagePreference } from './languagePreference';
import { createGuestWaitingRoomController } from '../waitingRoom';

function Login({ languages }) {
    const history = useHistory();
    const [form, setForm] = useState({
        language: null,
        displayName: null,
    });
    const [isModeratorPresent, setIsModeratorPresent] = useState(false);
    const [waitingRoom, setWaitingRoom] = useState(false);
    const [waitingRoomError, setWaitingRoomError] = useState(null);
    const waitingRoomControllerRef = useRef(null);
    const joinRoomRef = useRef(null);
    const myPubnubId = pubnub.userId;
    const activeWaitingRoom = window.activeWaitingRoom;
    const allowJoinWithoutModerator = window.allowJoinWithoutModerator;
    const canAttemptJoin = isModeratorPresent || allowJoinWithoutModerator;

    useEffect(() => {
        joinRoomRef.current = joinRoom;
    });

    useEffect(() => {
        const controller = createGuestWaitingRoomController({
            client: pubnub,
            room: window.room,
            participantId: myPubnubId,
            clientType: 'input',
            onModeratorPresence: setIsModeratorPresent,
            onAdmit: () => joinRoomRef.current(),
            onDeny: () => {
                setWaitingRoom(false);
                setWaitingRoomError(
                    'The moderator denied your request. You can update your details and try again.',
                );
            },
        });

        waitingRoomControllerRef.current = controller;
        controller.connect();

        return () => {
            controller.disconnect();
            waitingRoomControllerRef.current = null;
        };
    }, [myPubnubId]);

    useEffect(() => {
        const preferredLanguage = getGatewayLanguagePreference(languages);
        if (preferredLanguage) {
            setForm((currentForm) =>
                currentForm.language ? currentForm : { ...currentForm, language: preferredLanguage },
            );
        }
    }, [languages]);

    const handleLanguage = (language) => {
        setForm({ ...form, language: language });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        const { displayName } = form;

        if (validateLogin()) {
            if (activeWaitingRoom && isModeratorPresent) {
                setWaitingRoomError(null);
                setWaitingRoom(true);
                await waitingRoomControllerRef.current?.request(displayName);
            } else {
                joinRoom();
            }
        } else {
            alert('Please fill inputs to continue');
        }
    };

    const joinRoom = () => {
        const { displayName, language } = form;
        history.push(`/app`, {
            userName: displayName,
            language: language.value,
            gender: 'male',
            roomName: window.room,
            apiToken: window.apiToken,
            logoHeader: window.logoHeader,
            languagesPreTranslate: window.languagesPreTranslate,
            languages,
        });
    };

    const validateLogin = () => {
        let valid = true;
        Object.keys(form).forEach((item) => {
            if (!form[item] || form[item] === '') valid = false;
        });
        return valid;
    };

    return (
        <div className="wrapper-login">
            <div className="wrapper-content">
                <div className="div-tests-button"></div>
                <div className="wrapper-login-modal">
                    <div className="wrapper-logo">
                        <img src={`${window.logoLogin}`} alt="WWTC" />
                    </div>
                    <form className="form-modal" onSubmit={handleLogin}>
                        <div className="title-welcome">
                            <h2>Welcome</h2>
                        </div>
                        {!waitingRoom ? (
                            <div className="wrapper-login-form">
                                <div className="login-inputs">
                                    <label htmlFor="language-select" className="visually-hidden">
                                        Select your preferred language
                                    </label>
                                    <Select
                                        id="language-select"
                                        handleChange={handleLanguage}
                                        placeholder="Select your language"
                                        data={languages}
                                        value={form.language || ''}
                                        showLanguageServices={true}
                                        aria-label="Select your preferred language for the session"
                                    />
                                </div>
                                <div className="login-inputs">
                                    <label htmlFor="display-name" className="visually-hidden">
                                        Display name
                                    </label>
                                    <input
                                        id="display-name"
                                        className="Input"
                                        type="text"
                                        placeholder="Display name"
                                        value={form.displayName || ''}
                                        onChange={(e) =>
                                            setForm({ ...form, displayName: e.target.value })
                                        }
                                        style={{ width: 'calc(100% - 4px)' }}
                                        aria-label="Enter your display name"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div style={{ marginTop: 36, marginBottom: 36 }}>
                                <p style={{ textAlign: 'center' }}>
                                    Please wait. The Moderator will let you in shortly
                                </p>
                            </div>
                        )}
                        {!waitingRoom && (
                            <div className="wrapper-button-actions">
                                <button
                                    onClick={handleLogin}
                                    type="submit"
                                    className={`Button ${
                                        canAttemptJoin
                                            ? 'Button-primary'
                                            : 'Button-gray'
                                    } Button-block`}
                                    disabled={!canAttemptJoin}
                                >
                                    connect now
                                </button>
                            </div>
                        )}
                        {waitingRoomError && !waitingRoom && (
                            <div className="error-message" role="alert">
                                <p style={{ textAlign: 'center' }}>{waitingRoomError}</p>
                            </div>
                        )}
                        {!waitingRoom && !canAttemptJoin && (
                            <div className="error-message">
                                <p style={{ textAlign: 'center' }}>Moderator is not present</p>
                            </div>
                        )}
                        <div className="wrapper-return-home d-flex justify-content-center">
                            <a className="link" href="/">
                                Return to Home Screen
                            </a>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;
